<template>
    <div class="sw-modal">
        <div v-click-outside="close" v-on:click="menuIndex = -1; showExistingAgents = false"
             class="sw-modal-content edit-variables">

            <!--  ----------------------- -->
            <!--  Listing Agents view     -->
            <!--  ----------------------- -->
            <template v-if="isListingAgents">
                <div class="row sw-modal-heading">
                    <div class="col">
                        <h4><i class="fas fa-hands-helping"></i>Edit your Agents</h4>
                    </div>
                </div>
                <div class="sw-modal-body">
                    <div class="row">
                        <div class="col-12">
                            This list can be used in an <b>automation condition</b> to identify bookings
                            made with Agent details instead of guest details (by comparing against the email
                            address of the booker).
                        </div>
                    </div>
                    <table class="variables-values">
                        <thead>
                        <th class="markdown-syntax__hdr-element">Name</th>
                        <th class="markdown-syntax__hdr-element">Email</th>
                        <th class="markdown-syntax__hdr-syntax"></th>
                        </thead>
                        <tr v-for="(agent, index) in agents" v-bind:key="index">
                            <td>{{agent.name}}</td>
                            <td><template v-if="isDomainValue(agent.domainOrEmail)">***@</template>{{agent.domainOrEmail}}</td>
                            <td v-on:click.stop="toggleAgentMenu(index)">
                                <i class="fas fa-ellipsis-h"></i>
                                <div v-if="isShowAgentMenu(index)" class="variables-values__actions">
                                    <ul>
                                        <li v-on:click.stop="editAgent(agent)"><span><i class="fas fa-edit"></i></span> Edit
                                        </li>
                                        <li v-on:click.stop="confirmDeleteAgent(agent)"><span><i
                                                class="fas fa-trash-alt"></i></span> Delete
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!error" class="variables-values__add">
                            <td colspan="3">
                                <a v-on:click.stop="addAgent(false)" class="variables-values__add-button">+</a>
                            </td>
                        </tr>
                    </table>

                </div>
                <div class="sw-modal-actions">
                    <div v-if="waiting">
                        <Spinner/>
                    </div>
                    <div v-else class="row">
                        <div v-if="error" class="col-12">
                            <div class="alert alert-danger" role="alert">
                                {{error}}
                            </div>
                        </div>
                        <div class="col text-center">
                            <button class="btn btn-primary" v-on:click="close">Close</button>
                        </div>
                    </div>
                </div>
            </template>


            <!--  ----------------------- -->
            <!--  Editing an agent view   -->
            <!--  ----------------------- -->
            <template v-if="isEditingAgent">
                <div class="edit-form-component">
                    <div class="row sw-modal-heading">
                        <div class="col">
                            <h4><i class="fas fa-hands-helping"></i>Editing an Agent</h4>
                        </div>
                    </div>
                    <div class="sw-modal-body">
                        <ValidationObserver ref="agentForm">
                            <form>
                                <div class="row question">
                                    <div class="col-12 question__question">
                                        Name
                                    </div>
                                    <div class="col-12 edit-variable__name">
                                        <ValidationProvider rules="required|min:3" v-slot="{ errors }">
                                            <input ref="editAgentName"
                                                   v-model="editingAgent.name"
                                                   v-on:click.stop="openExistingAgentsList()"
                                                   class="short-question">
                                            <div v-if="agentsToCopy.length > 0" v-on:click.stop="toggleShowingExistingAgents"
                                                 class="edit-variable__use-existing">
                                                use existing
                                                <i v-if="!showExistingAgents" class="fa fa-chevron-down"
                                                   aria-hidden="true"></i>
                                                <i v-if="showExistingAgents" class="fa fa-chevron-up"
                                                   aria-hidden="true"></i>
                                            </div>
                                            <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                        <div class="auto-complete" v-show="showExistingAgents">
                                            <div class="auto-complete__inner">
                                                <template v-if="agentsToCopy.length > 0">
                                                    <div class="text-note">Copy an existing Agent</div>
                                                    <ul>
                                                        <li v-for="(agent, index) in agentsToCopy" v-bind:key="index"
                                                            v-on:click="copyAgent(agent)">
                                                            <div>{{agent.name}}</div>
                                                            <div><span class="text-note">***@</span>{{agent.domainOrEmail}}</div>
                                                        </li>
                                                    </ul>
                                                </template>
                                                <template v-else>
                                                    <div class="text-note">No Agents to copy</div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row question">
                                    <div class="col-12 question__question">
                                        Email or Domain
                                    </div>
                                    <div class="col-12 text-note">
                                        Match against one <b>exact email</b> - e.g. someone@agency.com<br>
                                        Match against <b>all emails from a domain</b> - eg. agency.com
                                    </div>
                                    <div class="col-12">
                                        <ValidationProvider mode="eager"
                                                            v-bind:rules="{ required: true, regex: /^(?:[a-zA-Z0-9._%+-]+@)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ }"
                                                            v-slot="{ errors }">
                                            <input v-model="editingAgent.domainOrEmail" type="text" class="short-question">
                                            <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div>
                            </form>
                        </ValidationObserver>
                        <div class="sw-modal-actions">
                            <div v-if="waiting || error || success" class="infobar"
                                 v-bind:class="{'infobar-info' : !error, 'infobar-error' : error}">
                                {{infoMessage}}
                            </div>
                            <div v-show="waiting && !success">
                                <Spinner/>
                            </div>
                            <div class="row" v-show="!waiting">
                                <div class="col text-center">
                                    <button class="btn btn-secondary" type="button" v-on:click.stop="cancel">Cancel
                                    </button>
                                    <button class="btn btn-primary" v-on:click.prevent="save()">
                                        <template v-if="isEditingExistingAgent">Save</template>
                                        <template v-else>Add</template>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

        </div>

        <confirm-dialog v-bind:message="confirmDeleteMessage" v-bind:callbackPrimaryName="'delete-agent'"
                        v-bind:callbackPrimaryReference="editingAgent.reference" v-if="showConfirmDeleteAgent"/>
    </div>
</template>

<script>
    import ConfirmDialog from '../ConfirmDialog.vue'
    import Spinner from '../Spinner.vue'
    import {ValidationObserver, ValidationProvider} from 'vee-validate'
    // import Tooltip from '../Tooltip'

    const LISTING_AGENTS = 0, EDITING_AGENT = 1;

    export default {
        name: 'EditAgents',
        components: {
            ConfirmDialog, Spinner, ValidationObserver, ValidationProvider//, Tooltip
        },
        props: {
            establishment: Object
        },
        data: function () {
            return {
                systemAgents: [],
                agentsToCopy: [],
                agents: [],
                mode: LISTING_AGENTS,
                menuIndex: -1,
                editingAgent: null,
                showExistingAgents: false,
                showConfirmDeleteAgent: false,
                waiting: false,
                infoMessage: null,
                success: null,
                error: null,
            }
        },
        computed: {
            isListingAgents: function () {
                return this.mode === LISTING_AGENTS;
            },
            isEditingAgent: function () {
                return this.mode === EDITING_AGENT;
            },
            isEditingExistingAgent: function () {
                return (this.editingAgent && this.editingAgent.reference);
            },
            confirmDeleteMessage: function () {
                return "Are you sure you want to <b>delete the agent '" + this.editingAgent.name + "'</b>?";
            },
        },
        methods: {
            toggleAgentMenu: function (index) {
                if (this.menuIndex === index) {
                    this.menuIndex = null;
                } else {
                    this.menuIndex = index;
                }
            },
            isShowAgentMenu: function (index) {
                return this.menuIndex === index;
            },
            isDomainValue: function(domainOrEmail) {
                return domainOrEmail.indexOf("@") < 0;
            },
            editAgent: function (agent) {
                this.mode = EDITING_AGENT;
                this.menuIndex = -1;
                this.editingAgent = {...agent};
                this.$nextTick(() => {
                    this.$refs.editAgentName.focus();
                });
            },
            openExistingAgentList: function() {
                if (this.agentsToCopy.length > 0) {
                    this.showExistingAgents = true;
                }
            },
            toggleShowingExistingAgents: function () {
                this.showExistingAgents = !this.showExistingAgents;
                this.$refs.editAgentName.focus();
            },
            confirmDeleteAgent: function (agent) {
                this.menuIndex = -1;
                this.editingAgent = agent;
                this.showConfirmDeleteAgent = true;
                console.log("deleting agent: " + agent.name);
            },
            addAgent: function () {
                this.mode = EDITING_AGENT;
                this.menuIndex = -1;
                this.editingAgent = {name: "", domainOrEmail: ""};
                this.$nextTick(() => {
                    this.$refs.editAgentName.focus();
                });
            },
            cancel: function () {
                this.mode = LISTING_AGENTS;
                this.editingAgent = null;
            },
            prepareAgentLists: function () {
                this.agentsToCopy.splice(0, this.agentsToCopy.length);
                this.agentsToCopy = [...this.systemAgents];
                for (let agent of this.agents) {
                    this.removeUsedAgentFromListToCopy(agent);
                }
                this.sortAgentList(this.systemAgents);
                this.sortAgentList(this.agentsToCopy);
                this.sortAgentList(this.agents);
            },
            removeUsedAgentFromListToCopy: function (agent) {
                for (let i = 0; i < this.agentsToCopy.length; i++) {
                    if (agent.domainOrEmail === this.agentsToCopy[i].domainOrEmail) {
                        this.agentsToCopy.splice(i, 1);
                    }
                }
            },
            addDeletedAgentToListToCopyIfSystemAgent: function (agent) {
                let added = false;
                for (let i = 0; i < this.systemAgents.length; i++) {
                    if (agent.domainOrEmail === this.systemAgents[i].domainOrEmail) {
                        this.agentsToCopy.push(agent);
                        added = true;
                        break;
                    }
                }
                if (added) {
                    this.sortAgentList(this.agentsToCopy);
                }
            },
            sortAgentList: function (agents) {
                agents.sort((first, second) => {
                    return first < second
                });
            },
            fetchSystemAgents: async function () {
                this.waiting = true;
                this.error = null;

                try {
                    const response = await this.$http.get('/guest-management/v1/data/otas', {
                        withCredentials: true
                    });
                    console.log(response);
                    this.systemAgents.splice(0, this.agentsToCopy.length);
                    if (response.data) {
                        for (let agent of response.data) {
                            this.systemAgents.push(agent);
                        }
                    }
                    this.sortAgents();
                } catch (error) {
                    console.log(error);
                    if (error.response) {
                        this.error = error.response.data.message
                    } else {
                        this.error = "An unknown error occurred while trying to fetch Agents"
                    }
                } finally {
                    console.log("Finished");
                    this.waiting = false;
                }
            },
            fetchAgents: async function () {
                this.waiting = true;
                this.error = null;

                try {
                    const response = await this.$http.get('/guest-management/v1/establishments/' + this.establishment.reference + '/otas', {
                        withCredentials: true
                    });
                    console.log(response);
                    this.agents.splice(0, this.agents.length);
                    if (response.data) {
                        for (let agent of response.data) {
                            this.agents.push(agent);
                        }
                    }
                    this.sortAgents();
                } catch (error) {
                    console.log(error);
                    if (error.response) {
                        this.error = error.response.data.message
                    } else {
                        this.error = "An unknown error occurred while trying to fetch Agents"
                    }
                } finally {
                    console.log("Finished");
                    this.waiting = false;
                }
            },
            sortAgents: function () {
                this.agents.sort(function (a, b) {
                    return a.name.localeCompare(b.name);
                });
            },
            copyAgent: function (agent) {
                this.editingAgent.name = agent.name;
                this.editingAgent.domainOrEmail = agent.domainOrEmail;
                this.showExistingAgents = false;
            },
            save: function () {
                this.$refs.agentForm.validate().then(success => {
                    console.log("Validated:" + success);
                    if (!success) {
                        return
                    }

                    this.waiting = true;
                    this.error = false;

                    const body = {
                        name: this.editingAgent.name,
                        domainOrEmail: this.editingAgent.domainOrEmail,
                    };

                    if (typeof this.editingAgent.reference === 'undefined') {
                        this.infoMessage = "Creating agent";

                        this.$http.post('/guest-management/v1/establishments/' + this.establishment.reference + '/otas', body
                        ).then(response => {
                            console.log("Agent created:")
                            console.log(response);
                            this.editingAgent.reference = response.data.reference;
                            this.agentAdded(this.editingAgent);
                            this.sortAgents();
                            this.infoMessage = "Agent created";
                            this.success = true;
                            setTimeout(() => {
                                this.clearEditingSettings();
                            }, 2000);
                        }).catch(error => {
                            this.error = true;
                            this.waiting = false;
                            if (error.response) {
                                this.infoMessage = "Error: " + error.response.data.message
                            } else {
                                this.infoMessage = "Error creating agent";
                            }
                        });
                    } else {
                        this.infoMessage = "Saving agent";

                        body.reference = this.editingAgent.reference;

                        this.$http.put('/guest-management/v1/establishments/' + this.establishment.reference + '/otas/' + this.editingAgent.reference, body
                        ).then(response => {
                            console.log("Agent updated:")
                            console.log(response);
                            this.agentUpdated(this.editingAgent);
                            this.sortAgents();
                            this.infoMessage = "Agent updated";
                            this.success = true;
                            setTimeout(() => {
                                this.clearEditingSettings();
                            }, 2000);
                        }).catch(error => {
                            this.error = true;
                            this.waiting = false;
                            if (error.response) {
                                this.infoMessage = "Error: " + error.response.data.message
                            } else {
                                this.infoMessage = "Error updating agent";
                            }
                        });
                    }
                })
            },
            clearEditingSettings: function () {
                this.success = false;
                this.error = false;
                this.waiting = false;
                this.infoMessage = null;
                this.mode = LISTING_AGENTS;
            },
            agentAdded: function (agent) {
                this.agents.push(agent);
                this.removeUsedAgentFromListToCopy(agent);
            },
            agentUpdated: function (agent) {
                for (let i = 0; i < this.agents.length; i++) {
                    let existing = this.agents[i];
                    if (existing.reference === agent.reference) {
                        this.agents.splice(i, 1, agent);
                        return;
                    }
                }
            },
            agentDeleted: function (reference) {
                for (let i = 0; i < this.agents.length; i++) {
                    let existing = this.agents[i];
                    if (existing.reference === reference) {
                        this.addDeletedAgentToListToCopyIfSystemAgent(this.agents[i]);
                        this.agents.splice(i, 1);
                        return;
                    }
                }
            },
            deleteAgent: async function (reference) {
                try {
                    if (reference) {
                        this.waiting = true;
                        const response = await this.$http.delete('/guest-management/v1/establishments/' + this.establishment.reference + '/otas/' + this.editingAgent.reference, {
                            withCredentials: true
                        });
                        console.log(response);
                        this.agentDeleted(this.editingAgent.reference);
                    }
                } catch (error) {
                    console.log(error);
                    if (error.response) {
                        this.error = "Error: " + error.response.data.message
                    } else {
                        this.error = "Error deleting agent";
                    }
                } finally {
                    this.showConfirmDeleteAgent = false;
                    this.editingAgent = null;
                    this.waiting = false;
                }

            },
            closePreview: function () {
                this.mode = LISTING_AGENTS;
            },
            close: function () {
                console.log("close edit Agents");
                console.log(this.editingAgent);
                this.$root.$emit('finished-editing-establishment-agents');
            },
        },
        mounted: async function () {
            await Promise.all([
                this.fetchSystemAgents(),
                this.fetchAgents()
            ]).then(() => {
                this.prepareAgentLists();
            });

            this.$root.$on('delete-agent', this.deleteAgent);
        },
        beforeDestroy () {
            this.$root.$off('delete-agent', this.deleteAgent);
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../styles/layout/forms";
    @import "../../styles/components/modal";
    @import "../../styles/components/edit_form_component";
    @import "../../styles/components/variables";
    @import "../../styles/components/edit_variables";
    @import "../../styles/components/question_answer.scss";
</style>
